import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Upload } from 'lucide-react';
import TranscriptInput from './TranscriptInput';
import VideoUploadInput from './VideoUploadInput';
import { 
  startMultipartUpload, 
  generatePartUrls, 
  uploadMultipartChunk, 
  completeMultipartUpload
} from '../features/contentCompliance/contentComplianceSlice';
import './UploadVideoPage.css';

function UploadVideoPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const [title, setTitle] = useState('');
  const [showTranscriptInput, setShowTranscriptInput] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [videoFile, setVideoFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  const partUploadProgress = useSelector((state) => state.contentCompliance.partUploadProgress);
  const complianceStatus = useSelector((state) => state.contentCompliance.status);
  
  // Function to strip HTML tags and count words
  const stripHtmlAndCountWords = (htmlString) => {
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    const textContent = doc.body.textContent || '';
    return textContent.trim().split(/\s+/).filter(Boolean).length;
  };
  
  // Calculate overall upload progress
  useEffect(() => {
    if (videoFile && isUploading) {
      const chunkSize = 5 * 1024 * 1024; // 5MB
      const totalChunks = Math.ceil(videoFile.size / chunkSize);
      let totalProgress = 0;

      for (const partNumber in partUploadProgress) {
        const part = partUploadProgress[partNumber];
        if (part.status === 'succeeded') {
          totalProgress += 100;
        } else if (part.status === 'loading') {
          totalProgress += part.progress || 0;
        }
      }
      const overallProgress = totalChunks > 0 ? Math.round((totalProgress / totalChunks)) : 0;
      setUploadProgress(overallProgress);
    } else if (!isUploading) {
      setUploadProgress(0);
    }
  }, [partUploadProgress, videoFile, isUploading]);

  const handleUploadVideo = async () => {
    if (!videoFile) return;

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const startUploadResponse = await dispatch(startMultipartUpload({
        fileName: videoFile.name,
        fileType: videoFile.type,
        fileSize: videoFile.size,
      })).unwrap();

      const newUploadId = startUploadResponse?.data?.upload_id;
      const s3Key = startUploadResponse?.data?.file_name;
      if (!newUploadId || !s3Key) {
        throw new Error("Failed to get upload_id or file_name from startMultipartUpload.");
      }

      // Split file into chunks (5MB each)
      const chunkSize = 5 * 1024 * 1024;
      const totalChunks = Math.ceil(videoFile.size / chunkSize);
      const partNumbers = Array.from({ length: totalChunks }, (_, i) => i + 1);

      // Call POST /generate_part_urls (use s3Key returned from start)
      const generateUrlsResponse = await dispatch(generatePartUrls({
        fileName: s3Key,
        uploadId: newUploadId,
        partNumbers: partNumbers,
      })).unwrap();

      if (!generateUrlsResponse || !generateUrlsResponse.data || !generateUrlsResponse.data.urls) {
        throw new Error("Failed to get part URLs from generatePartUrls.");
      }
      const presignedUrls = generateUrlsResponse.data.urls;
      const eTagsArray = [];

      const uploadPromises = presignedUrls.map(async (partInfo) => {
        const partNumber = partInfo.part_number;
        const url = partInfo.url;
        const start = (partNumber - 1) * chunkSize;
        const end = Math.min(start + chunkSize, videoFile.size);
        const chunk = videoFile.slice(start, end);

        const uploadResult = await dispatch(uploadMultipartChunk({
          url: url,
          chunk: chunk,
          partNumber: partNumber,
          onUploadProgress: () => {},
        })).unwrap();

        const resultETag = { ETag: uploadResult.ETag, PartNumber: uploadResult.partNumber };
        eTagsArray.push(resultETag);
        return uploadResult;
      });

      await Promise.all(uploadPromises);

      // After all parts are uploaded, call completeMultipartUpload
      const partsForCompletion = eTagsArray.sort((a, b) => a.PartNumber - b.PartNumber);
      const completeUploadResponse = await dispatch(completeMultipartUpload({
        fileName: s3Key,
        uploadId: newUploadId,
        parts: partsForCompletion,
        title: title || null,
      })).unwrap();

      console.log('Multipart upload completed:', completeUploadResponse);

      setIsUploading(false);
      
      // Navigate to videos1 page after successful upload
      navigate('/dashboard/videos1');

    } catch (error) {
      console.error('Error in video upload workflow:', error);
      setIsUploading(false);
      setUploadProgress(0);
      alert('Failed to upload video. Please try again.');
    }
  };

  const handleUploadContent = async () => {
    if (!transcript || stripHtmlAndCountWords(transcript) < 10) {
      alert('Please enter at least 10 words in the transcript.');
      return;
    }

    try {
      
      // TODO: Call the API to upload the content
      
      // Navigate to videos1 page after successful upload
      navigate('/dashboard/videos1');
      
    } catch (error) {
      console.error("Content upload failed:", error);
      alert('Failed to upload content. Please try again.');
    }
  };

  return (
    <div className="upload-video-page">
      <div className="content-container">
        <section className="compliance-card">
          {/* Title Input */}
          <div className="title-input-container">
            <label htmlFor="videoTitle" className="title-label">Title</label>
            <input
              id="videoTitle"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter a title (optional)"
              className="title-input"
            />
          </div>

          {/* Tabs for Transcript / Video */}
          <div className="input-tabs">
            <button
              type="button"
              className={`input-tab ${showTranscriptInput ? 'active' : ''}`}
              onClick={() => setShowTranscriptInput(true)}
            >
              Transcript
            </button>
            <button
              type="button"
              className={`input-tab ${!showTranscriptInput ? 'active' : ''}`}
              onClick={() => setShowTranscriptInput(false)}
            >
              Video
            </button>
          </div>

          {/* Input area */}
          <div className="input-area">
            {showTranscriptInput ? (
              <TranscriptInput
                transcript={transcript}
                setTranscript={setTranscript}
              />
            ) : (
              <VideoUploadInput
                videoFile={videoFile}
                setVideoFile={setVideoFile}
              />
            )}
          </div>

          {/* Footer buttons */}
          <div className="button-row">
            {/* Upload Content button for transcript tab */}
            {showTranscriptInput && (
              <button
                className="upload-button"
                type="button"
                onClick={handleUploadContent}
                disabled={stripHtmlAndCountWords(transcript) < 10 || complianceStatus === 'loading'}
              >
                <Upload size={16} />
                {complianceStatus === 'loading' ? 'Uploading...' : 'Upload Content'}
              </button>
            )}

            {/* Upload Video button for video tab */}
            {!showTranscriptInput && (
              <button
                className="upload-button"
                type="button"
                onClick={handleUploadVideo}
                disabled={!videoFile || isUploading}
              >
                <Upload size={16} />
                {isUploading ? `Uploading... ${uploadProgress}%` : 'Upload Video'}
              </button>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default UploadVideoPage;

